import {
  Fragment,
  MutableRefObject,
  Ref,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import css from "./ChatHistory.module.css"
import {
  buildChatRows,
  GroupableRow,
  hasTheId,
  isSlickAIGroup,
  Message,
  MessageDisplayType,
  MessageGroup,
} from "@/reducers/messagesSlice"
import { Chat } from "@/reducers/chatsSlice"
import { selectLiveTurn } from "@/reducers/agentPipelineSlice"
import ChatHelpers from "../lib/chatHelpers"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  selectLocationJidFromChatJid,
  selectRouteJidFromChatJid,
} from "@/reducers/selectors"
import { selectLocation } from "@/reducers/locationsSelectors"
import ChatMap from "./maps/ChatMap"
import ChatSidePanel from "@/components/chat/ChatSidePanel"
import { useWidgetTabs } from "@/hooks/useWidgetTabs"
import Loader from "./Loader"
import { DriverContext } from "@/contexts/DriverContext"
import { DriverWorkspaceProvider } from "@/contexts/DriverWorkspaceContext"
import ChatInput, { ChatInputTextAreaRef } from "./ChatInput"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import DropZone from "./ui/message/DropZone"
import MessageRow from "./chat/MessageRow"
import TopDayMarker from "./TopDayMarker"
import {
  messagesForSlickAiPane,
  onlyDriverConversation,
} from "@/lib/agentThread"
import { shouldFetchOlderMessages } from "@/lib/olderMessages"
import {
  selectHasRoomSyncFailed,
  selectIsRoomSyncFinished,
  selectRoomSyncFailureReason,
} from "@/reducers/roomSyncStatusSelectors"
import * as Sentry from "@sentry/react"
import { Trans, useTranslation } from "react-i18next"
import ErrorWithRetry from "./ErrorWithRetry"
import { retrySyncSingleRoom } from "@/reducers/roomSyncStatusSliceThunks"
import ImageGallery from "./photoView/ImageGallery"
import { findRoute2g } from "@/reducers/routes2gSlice"
import PDFViewer from "./pdfView/PDFViewer"
import {
  isImageGalleryVisible,
  isPDFViewerVisible,
  selectRouteAlert,
} from "@/reducers/overlaysSlice"
import NewSlickAIChatState from "./chat/SlickAIChatState"
import { AIChatView } from "@/components/ai-chat/AIChatView"
import { useFeatureFlagEnabled } from "posthog-js/react"
import posthog from "posthog-js"
import PinnedMessage from "./pinnedMessage/PinnedMessage"
import useMessageRows from "../hooks/useMessageRows"
import useDriverContext from "../hooks/useDriverContext"
import useChatMessageFetcher from "../hooks/useChatMessageFetcher"
import { VList, VListHandle } from "virtua"
import ScrollBottomButton from "./ScrollBottomButton"
import ShowMobileMapButton from "./maps/ShowMobileMapButton"
import { ResizablePanel, ResizablePanelGroup } from "./ui/resizable"
import { isDriverRoomGroup } from "@/lib/slickAISilence"
import { useAgentForDriverPerChat } from "@/hooks/useAgentForDriverPerChat"
import { useDesktopMapHidden } from "@/hooks/useDesktopMapHidden"
import ShowDesktopMapButton from "@/components/maps/ShowDesktopMapButton"
import {
  MessageOptions,
  sendDriverAgentThreadMessage,
} from "@/reducers/chatsSliceThunks"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { isMobile } from "@/utils/detectDevice"
import { cn } from "@/lib/utils"
import ChatHeader from "@/components/ChatHeader"
import { useGpsDocumentLinksSync } from "@/hooks/useGpsDocumentLinksSync"
import DriverOverviewSection from "@/components/chat/DriverOverviewSection"
import { useMediaQuery } from "@react-hookz/web"
import ReferenceRouteAlert from "@/components/maps/ReferenceRouteAlert"
import LateAlert from "@/components/maps/LateAlert"
import FinishAlert from "@/components/maps/FinishAlert"
import { rowContainsMessageId } from "@/components/chat/utils"
import { AnalyzeOrderProvider } from "@/contexts/AnalyzeOrderContext"
import {
  AudioPlaybackContext,
  AudioPlaybackContextType,
} from "@/contexts/AudioPlaybackContext"
import AnalyzeOrder from "./maps/AnalyzeOrder"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import MessageSelectionBar from "@/components/ui/message/MessageSelectionBar"
import ForwardMessageDialog from "@/components/ui/message/ForwardMessageDialog"
import { useMessageForwarding } from "@/hooks/useMessageForwarding"
import { XmppContext } from "@/stream/xmppClient"
import UnreadSeparator from "@/components/chat/UnreadSeparator"
import useTabVisibility from "@/hooks/useTabVisibility"
import useSlickAISilencePredicate from "@/hooks/useSlickAISilencePredicate"
import SlickAIGroupRow, {
  SLICK_AI_GROUP_ATTRIBUTE,
} from "@/components/chat/SlickAIGroupRow"

/** The scroll anchor of a collapsed group. Only the scroller needs the DOM. */
const slickAIGroupElement = (groupId: string) =>
  document.querySelector(
    `[${SLICK_AI_GROUP_ATTRIBUTE}="${CSS.escape(groupId)}"]`,
  )

export type ChatHistoryHandle = {
  scrollToBottom: (smooth: boolean) => void
  shifting: MutableRefObject<boolean>
  setStickToTheBottom: (value: boolean) => void
}

type ChatProps = {
  chat: Chat
  chatHistoryRef: Ref<ChatHistoryHandle | null>
  chatInputRef: RefObject<ChatInputTextAreaRef>
  markMessageDisplayed: (message: Message) => void
  onFileDropped: (files: FileList) => void
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  onReturnToChatLists: () => void
  onOpenChatInfo: () => void
  onActiveChatChange: (chatJid: string) => void
  showHeader?: boolean
}

const ERROR_PLACEHOLDER = Symbol("error-placeholder")
const COMPOSING_PLACEHOLDER = Symbol("composing-placeholder")

export type PlaceholderType =
  | typeof ERROR_PLACEHOLDER
  | typeof COMPOSING_PLACEHOLDER

export type ChatRow = GroupableRow | PlaceholderType

const isPlaceholder = (row: any): row is PlaceholderType =>
  typeof row === typeof COMPOSING_PLACEHOLDER ||
  typeof row === typeof ERROR_PLACEHOLDER

export const MESSAGE_HIGHLIGHT_DURATION_MS = 1250

const ChatHistory = ({
  chat,
  chatHistoryRef,
  chatInputRef,
  markMessageDisplayed,
  onFileDropped,
  onSend,
  onReturnToChatLists,
  onOpenChatInfo,
  onActiveChatChange,
  showHeader = true,
}: ChatProps) => {
  const { t } = useTranslation()
  const { activeTab } = useAppSelector(selectTabs)
  const { myJid, client } = useContext(XmppContext)

  const dispatch = useAppDispatch()
  const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
  useGpsDocumentLinksSync(chat)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(chat.jid))
  const locationJid = useAppSelector(selectLocationJidFromChatJid(chat.jid))
  const currentLocation = useAppSelector(selectLocation(locationJid))
  const route2g = useAppSelector(findRoute2g(routeJid))
  const isRoomSyncFinished = useAppSelector(selectIsRoomSyncFinished(chat.jid))
  const hasRoomSyncFailed = useAppSelector(selectHasRoomSyncFailed(chat.jid))
  const roomSyncFailureReason = useAppSelector(
    selectRoomSyncFailureReason(chat.jid),
  )
  // `useFeatureFlagEnabled` is undefined on the first render (it resolves in a
  // post-mount effect), which made the chat flash the classic VList view before
  // flipping to AIChatView. Fall back to the synchronous cached flag value so
  // the very first render is already correct.
  const aiStreamingFlagEnabled =
    useFeatureFlagEnabled("ai-streaming") ??
    posthog.isFeatureEnabled("ai-streaming")
  const aiStreamingEnabled =
    window.anonymousXMPP === true || aiStreamingFlagEnabled === true
  const isAIChat = aiStreamingEnabled && ChatHelpers.isAIChatRoom(chat.jid)
  // FTUE-specific composer treatment (assistant placeholder + "#" hint) is
  // gated by the ai-suggestions flag — same condition AIChatView uses — so the
  // chat behaves exactly as before when the flag is off.
  const aiSuggestionsFlagEnabled =
    useFeatureFlagEnabled("ai-suggestions") ??
    posthog.isFeatureEnabled("ai-suggestions")
  const ftueEnabled =
    isAIChat &&
    (window.anonymousXMPP === true || aiSuggestionsFlagEnabled === true)
  const liveTurn = useAppSelector((state) => selectLiveTurn(state, chat.jid))
  const isAgentWorking = liveTurn !== undefined
  const messageHighlightTimeoutRef = useRef<NodeJS.Timeout>()
  const [messageHighlight, setMessageHighlight] = useState<
    { messageId: string; animationKey: string } | undefined
  >()
  const isScrollingNotByUser = useRef(false)
  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  const shouldRenderMap = Boolean(currentLocation)

  const mapOnMobile = shouldRenderMap && (isMobile || isSmallScreen)
  // `isMobile` reads the user agent, so it misses a narrowed desktop window
  // and a tablet asking for the desktop site. Anything narrow stacks the
  // conversation and the widgets instead of squeezing them side by side.
  const stackLayout = isMobile || isSmallScreen

  // The whole driver workspace — split layout, widget panel, agent column —
  // rides on one flag. Off, every driver room renders the way it did before
  // the branch: one thread, the full header and the map column.
  const agentPerChatEnabled = useAgentForDriverPerChat()

  // One layout for every driver room: Slick AI (dispatcher ↔ agent) on the
  // left, widgets on the right with Chat = the conversation with the driver.
  const isDriverRoomChat = isDriverRoomGroup(accountChat?.group) && !isAIChat
  const splitDriverChat = agentPerChatEnabled && isDriverRoomChat
  // The tabbed widget panel can be closed entirely (conversation full-width).
  const { hidden: widgetsHidden } = useWidgetTabs()
  const showWidgetColumn = splitDriverChat && !widgetsHidden

  // Flag off: the pre-branch desktop map column, straight from `main`.
  const mapOnDesktop = shouldRenderMap && !isMobile && !isSmallScreen
  const hideMapButtonEnabled =
    (useFeatureFlagEnabled("hide-map-button") ??
      posthog.isFeatureEnabled("hide-map-button")) === true
  const [mapHidden, setMapHidden] = useDesktopMapHidden()
  const mapHiddenByUser = hideMapButtonEnabled && mapHidden
  const showDesktopMapPanel =
    !agentPerChatEnabled && mapOnDesktop && !mapHiddenByUser
  const showDesktopMapButton =
    !agentPerChatEnabled &&
    hideMapButtonEnabled &&
    Boolean(routeJid) &&
    mapOnDesktop &&
    mapHidden

  const lastMessageMarkedAsDisplayedRef = useRef<string | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)
  const highlightDuplicateStopRef = useRef<((stopId: string) => void) | null>(
    null,
  )
  const messages = useMessageRows(chat.jid)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  // Folding Slick's automations into one quiet line rides on the same flag as
  // the badge and the popup — see `lib/slickAISilence.ts`. It also drives the
  // unread-count calc below: a silenced automation doesn't bump the separator.
  const silenceSlickAI = useSlickAISilencePredicate()(chat.jid)
  // Local on purpose: leaving the chat folds the groups back up. The dispatcher
  // opens them only when they need to.
  const [expandedSlickAIGroups, setExpandedSlickAIGroups] = useState<
    Set<string>
  >(new Set())
  // Where the clicked header sat on screen at the moment of the click.
  const slickAIToggleRef = useRef<{
    groupId: string
    screenTop: number
  } | null>(null)

  // Split off: the automations sit in the driver room, where they are noise —
  // fold them. Split on: this list IS the Slick AI column, where Slick's own
  // messages are the whole point, so folding would hide the dispatcher's
  // agent conversation. The driver-visible alerts then sit in the driver card;
  // silencing them there is a separate job.
  const foldSlickAIAutomations = silenceSlickAI && !splitDriverChat

  const toggleSlickAIGroup = useCallback((groupId: string) => {
    const element = slickAIGroupElement(groupId)
    slickAIToggleRef.current = {
      groupId,
      screenTop: element?.getBoundingClientRect().top ?? 0,
    }

    setExpandedSlickAIGroups((previous) => {
      const next = new Set(previous)
      if (!next.delete(groupId)) next.add(groupId)
      return next
    })
  }, [])
  const { onFetchMoreMessages } = useChatMessageFetcher(chat, messages)

  // Message forwarding functionality
  const messageForwarding = useMessageForwarding(messages)

  const [showUnreadSeparator, setShowUnreadSeparator] = useState(true)
  const firstUnreadMessageIdRef = useRef<string | null>(null)

  const isTabVisible = useTabVisibility({
    onVisible: useCallback(() => {
      if (messages.length > 0) {
        const firstUnreadMessageId = ChatHelpers.firstUnreadMessageId(
          { jid: chat.jid, messages },
          myJid.toString(),
          { silenceSlickAI },
        )

        if (firstUnreadMessageId) {
          firstUnreadMessageIdRef.current = firstUnreadMessageId
          setShowUnreadSeparator(true)
        }
      }
    }, [chat.jid, messages, myJid, silenceSlickAI]),
  })

  // Split on: Slick AI column. Split off: the room minus the dispatcher↔agent
  // conversation — the notes the backend stamped slick:agent stay (fuel alerts,
  // scan-check, delays), the Q&A goes, because without the column there is
  // nowhere to read it. See `lib/agentThread`.
  const layerFilteredMessages = useMemo(
    () => messagesForSlickAiPane(messages, splitDriverChat),
    [messages, splitDriverChat],
  )

  const driverVisibleMessages = useMemo(
    () => onlyDriverConversation(messages),
    [messages],
  )

  const handleAgentSend = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      dispatch(sendDriverAgentThreadMessage(trimmed, client, chat))
    },
    [dispatch, client, chat],
  )

  const rows = useMemo(() => {
    const rows: ChatRow[] = buildChatRows(layerFilteredMessages, {
      expandedImageGroups: expandedGroups,
      foldAutomations: foldSlickAIAutomations,
      expandedSlickAIGroups,
    })

    if (hasRoomSyncFailed) rows.push(ERROR_PLACEHOLDER)
    if (isAgentWorking) rows.push(COMPOSING_PLACEHOLDER)

    return rows
  }, [
    layerFilteredMessages,
    expandedGroups,
    hasRoomSyncFailed,
    isAgentWorking,
    foldSlickAIAutomations,
    expandedSlickAIGroups,
  ])

  // Calculate which row index should show the unread separator
  const unreadSeparatorRowIndex = useMemo(() => {
    if (!firstUnreadMessageIdRef.current || !showUnreadSeparator) return null

    // Find which row contains the first unread message by ID
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (isPlaceholder(row)) continue
      if (hasTheId(row, firstUnreadMessageIdRef.current)) return i
    }
    return null
  }, [rows, showUnreadSeparator])

  const hideUnreadSeparator = useCallback(() => {
    firstUnreadMessageIdRef.current = null
    setShowUnreadSeparator(false)
  }, [])

  const scrollToBottom = useCallback(
    (smooth: boolean = true) => {
      if (ref.current && rows.length > 0) {
        ref.current.scrollToIndex(rows.length - 1, {
          align: "end",
          smooth: smooth,
        })
        setShowScrollToBottomButton(false)
      } else {
        console.warn("scrollToBottom called but ref is null or rows are empty")
      }
    },
    [rows.length],
  )

  const setStickToTheBottom = useCallback(
    (value: boolean) => (shouldStickToBottom.current = value),
    [],
  )

  useImperativeHandle(
    chatHistoryRef,
    () => ({
      scrollToBottom,
      shifting,
      setStickToTheBottom,
    }),
    [scrollToBottom, setStickToTheBottom],
  )

  // --- Marking as displayed ---

  const checkIfMarkMessageAsDisplayed = useCallback(() => {
    if (ref.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      const lastVisibleItemIndex = ref.current.findEndIndex()
      const lastItemIsWithinView = lastVisibleItemIndex === rows.length - 1
      if (
        lastItemIsWithinView &&
        lastMessage &&
        lastMessage.id !== lastMessageMarkedAsDisplayedRef.current
      ) {
        markMessageDisplayed(lastMessage)
        lastMessageMarkedAsDisplayedRef.current = lastMessage.id
      }
    }
  }, [
    lastMessageMarkedAsDisplayedRef,
    markMessageDisplayed,
    messages,
    rows.length,
  ])

  // AIChatView doesn't use VList, so the scroll-based displayed check above
  // never fires for AI chats. Mark the last incoming message as displayed
  // whenever the AI chat is open and the tab is visible.
  useEffect(() => {
    if (!isAIChat) return
    if (!isTabVisible) return
    if (messages.length === 0) return

    const lastMessage = messages[messages.length - 1]
    if (lastMessage.id === lastMessageMarkedAsDisplayedRef.current) return

    markMessageDisplayed(lastMessage)
    lastMessageMarkedAsDisplayedRef.current = lastMessage.id
  }, [isAIChat, isTabVisible, messages, markMessageDisplayed])

  useLayoutEffect(
    function onAssistantChatState() {
      if (!liveTurn) return
      if (
        shouldStickToBottom.current ||
        ChatHelpers.isChatWithAssistant(chat)
      ) {
        scrollToBottom(false)
      }
    },
    [chat, liveTurn, scrollToBottom],
  )

  const expandImageGroup = useCallback(
    (messageOrMessageGroup: Message | MessageGroup) => {
      messageOrMessageGroup.displayType == MessageDisplayType.MessageGroup &&
        setExpandedGroups(
          (prev) =>
            new Set(prev.add((messageOrMessageGroup as MessageGroup).groupId)),
        )
    },
    [],
  )

  const messageIdRowIndex = useCallback(
    (messageId: string) => {
      return rows.findIndex((r) => {
        if (isPlaceholder(r)) {
          return false
        }

        return hasTheId(r, messageId)
      })
    },
    [rows],
  )

  const scrollChatToMessage = useCallback(
    (id: string) => {
      isScrollingNotByUser.current = true
      const chatIndex = messageIdRowIndex(id)
      if (chatIndex >= 0 && ref.current) {
        ref.current.scrollToIndex(chatIndex, { smooth: false, align: "center" })
        requestAnimationFrame(() => {
          isScrollingNotByUser.current = false
        })
      }
    },
    [messageIdRowIndex],
  )

  const setMessageAsHighlighted = useCallback(
    (messageId: string) => {
      setMessageHighlight({
        messageId,
        animationKey: Math.random().toString(),
      })
      if (messageHighlightTimeoutRef.current) {
        clearTimeout(messageHighlightTimeoutRef.current)
      }
      messageHighlightTimeoutRef.current = setTimeout(() => {
        setMessageHighlight(undefined)
      }, MESSAGE_HIGHLIGHT_DURATION_MS)
    },
    [messageHighlightTimeoutRef, setMessageHighlight],
  )

  const jumpAndHighlightMessage = useCallback(
    (messageId: string) => {
      scrollChatToMessage(messageId)
      setMessageAsHighlighted(messageId)
    },
    [scrollChatToMessage, setMessageAsHighlighted],
  )

  const chatHistoryContext = useMemo(() => {
    return {
      messageHighlight,
      jumpAndHighlightMessage,
      chatInputRef,
      expandImageGroup,
      isForwardingMode: messageForwarding.isForwardingMode,
      selectedMessageIds: messageForwarding.selectedMessageIds,
      enterForwardingMode: messageForwarding.enterForwardingMode,
      exitForwardingMode: messageForwarding.exitForwardingMode,
      toggleMessageSelection: messageForwarding.toggleMessageSelection,
      handleForwardMessages: messageForwarding.handleForwardMessages,
    }
  }, [
    messageHighlight,
    jumpAndHighlightMessage,
    chatInputRef,
    expandImageGroup,
    messageForwarding.isForwardingMode,
    messageForwarding.selectedMessageIds,
    messageForwarding.enterForwardingMode,
    messageForwarding.exitForwardingMode,
    messageForwarding.toggleMessageSelection,
    messageForwarding.handleForwardMessages,
  ])

  const renderMessageRow = (row: Message | MessageGroup, index: number) => {
    const messageOrMessageGroup = row

    const prevRow = rows[index - 1]
    const prevMessageOrMessageGroup = !isPlaceholder(prevRow)
      ? prevRow
      : undefined

    const nextRow = index < rows.length - 1 ? rows[index + 1] : undefined
    const nextMessageOrMessageGroup =
      nextRow && !isPlaceholder(nextRow) ? nextRow : undefined

    // Determine if the row is a message group (we are grouping images)

    const isGroup =
      messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup

    const key = isGroup
      ? messageOrMessageGroup.groupId
      : messageOrMessageGroup.id

    const shouldRenderUnreadSeparator = index === unreadSeparatorRowIndex

    // The fragment is the array element, so the key belongs here — on MessageRow
    // it was invisible to React and rows reconciled by index.
    return (
      <Fragment key={key}>
        {shouldRenderUnreadSeparator && (
          <UnreadSeparator
            unreadCount={ChatHelpers.unreadCountSinceMessage(
              messages,
              firstUnreadMessageIdRef.current,
              { silenceSlickAI },
            )}
            onHide={hideUnreadSeparator}
          />
        )}
        <MessageRow
          data-testid="message-row"
          data-index={index}
          previousRow={prevMessageOrMessageGroup}
          row={row}
          nextRow={nextMessageOrMessageGroup}
          accountChat={accountChat}
          isDriverRoomChat={splitDriverChat}
          composerSurface={splitDriverChat ? "agent" : "driver"}
        />
      </Fragment>
    )
  }

  // --- Rendering ---
  const renderVirtualRow = (row: ChatRow, index: number) => {
    if (!isPlaceholder(row) && isSlickAIGroup(row)) {
      const prevRow = rows[index - 1]

      return (
        <div data-index={index} key={`slickai-${row.groupId}`}>
          <SlickAIGroupRow
            group={row}
            previousRow={isPlaceholder(prevRow) ? undefined : prevRow}
            onToggle={() => toggleSlickAIGroup(row.groupId)}
          />
        </div>
      )
    }

    if (row === ERROR_PLACEHOLDER) {
      return (
        <div
          data-index={index}
          key="error-placeholder"
          className={css.chatLoader}
        >
          <ErrorWithRetry
            onRetry={() => dispatch(retrySyncSingleRoom(chat.jid))}
            errorMessage={
              roomSyncFailureReason
                ? t("app.chatHistory.errorLoadingWithReason", {
                    reason: roomSyncFailureReason,
                  })
                : t("app.chatHistory.errorLoading")
            }
          />
        </div>
      )
    }

    if (row === COMPOSING_PLACEHOLDER) {
      return (
        <div data-index={index} key="composing-placeholder">
          <NewSlickAIChatState
            chat={chat}
            aiStreamingEnabled={aiStreamingEnabled === true}
          />
        </div>
      )
    }

    return renderMessageRow(row, index)
  }

  const driverContext = useDriverContext(
    routeJid,
    route2g,
    highlightDuplicateStopRef,
  )

  const imageGalleryVisible = useAppSelector(isImageGalleryVisible)
  const imageGalleryDirectImages = useAppSelector(
    (state) => state.overlays.imageGallery.directImages,
  )
  const imageGalleryMetadataOverrides = useAppSelector(
    (state) => state.overlays.imageGallery.metadataOverrides,
  )
  // When an external host (e.g. CmrSearchDialog) is rendering the gallery,
  // skip the default in-chat instance to avoid mounting two PhotoView
  // portals at once — they race for stacking order between dev and prod.
  const imageGalleryExternalSource = useAppSelector(
    (state) => state.overlays.imageGallery.externalSource,
  )
  const PDFViewerVisible = useAppSelector(isPDFViewerVisible)
  const PDFViewerExternalSource = useAppSelector(
    (state) => state.overlays.PDFViewer.externalSource,
  )
  const routeAlert = useAppSelector(selectRouteAlert)

  const referenceRouteAlertVisible =
    routeAlert?.visible && routeAlert.type === "reference"

  const lateAlertVisible = routeAlert?.visible && routeAlert.type === "late"
  const routeFinishedAlertVisible =
    routeAlert?.visible && routeAlert.type === "routeFinished"

  const shifting = useRef(false)
  const effectEnabled = useRef(false)
  const [startFetching, setStartFetching] = useState(false)
  /** Automatic top-up of a thread too short to fill the viewport. */
  const backfillingRef = useRef(false)
  const ref = useRef<VListHandle>(null)
  const count = rows.length
  const startFetchedCountRef = useRef(-1)

  const ready = useRef(false)
  // Feeds the floating date pill that rides the top of the thread while you
  // scroll. The workspace shows a quiet inline date line instead, but outside
  // it this is how the thread has always been dated.
  const [isScrolling, setIsScrolling] = useState(false)
  const [firstVisibleIndex, setFirstVisibleIndex] = useState<number | null>(
    null,
  )
  const shouldStickToBottom = useRef(true)
  const [showScrollToBottomButton, setShowScrollToBottomButton] =
    useState(false)

  useEffect(() => {
    ready.current = true
  }, [])

  const handleScrollBottomButton = useCallback(() => {
    isScrollingNotByUser.current = true
    scrollToBottom(false)
    requestAnimationFrame(() => {
      isScrollingNotByUser.current = false
      checkIfMarkMessageAsDisplayed()
    })
  }, [checkIfMarkMessageAsDisplayed, scrollToBottom])

  const resetChatState = useCallback(() => {
    // Reset necessary state variables and refs when chat changes
    firstUnreadMessageIdRef.current = null
    shifting.current = false
    effectEnabled.current = false
    setStartFetching(false)
    backfillingRef.current = false
    setIsScrolling(false)
    setShowScrollToBottomButton(false)
    shouldStickToBottom.current = true
    startFetchedCountRef.current = -1
    isScrollingNotByUser.current = false
    lastMessageMarkedAsDisplayedRef.current = undefined
    slickAIToggleRef.current = null
    setExpandedSlickAIGroups(new Set())
  }, [])

  // This layout effect handles the state reset and initial scroll on chat change
  useLayoutEffect(() => {
    resetChatState()
    if (messageForwarding.isForwardingMode) {
      messageForwarding.exitForwardingMode()
    }

    const firstUnreadMessageId = ChatHelpers.firstUnreadMessageId(
      { jid: chat.jid, messages },
      myJid.toString(),
      { silenceSlickAI },
    )

    if (firstUnreadMessageId) {
      firstUnreadMessageIdRef.current = firstUnreadMessageId
      setShowUnreadSeparator(true)
    } else {
      firstUnreadMessageIdRef.current = null
      setShowUnreadSeparator(false)
    }

    // IMPORTANT: Defer scroll slightly to allow VList to potentially process the new data
    // useLayoutEffect runs after DOM mutation but before paint,
    // VList might still be updating its internal virtual state.
    const timerId = setTimeout(() => {
      if (isRoomSyncFinished) {
        // The jump to the newest message is ours, not the dispatcher's: flag it
        // the way the scroll-to-bottom button does, so the scroll it emits is
        // not read as "scrolled up to the oldest messages" and does not pull
        // another page of history the moment a room opens.
        isScrollingNotByUser.current = true
        scrollToBottom(false) // Use false for immediate jump on chat change
        requestAnimationFrame(() => {
          isScrollingNotByUser.current = false
          // Initial check if messages need marking displayed right after switching
          checkIfMarkMessageAsDisplayed()
        })
      } else {
        console.log(
          "Skipping initial scroll, room sync not finished for JID:",
          chat.jid,
        )
      }
    }, 0) // setTimeout 0 defers execution until after the current call stack
    const effectTimerId = setTimeout(() => {
      effectEnabled.current = true
    }, 500)
    return () => {
      clearTimeout(effectTimerId)
      clearTimeout(timerId)
    } // Cleanup the timeout
  }, [chat.jid, isRoomSyncFinished]) // Add dependencies

  const prevRowsLength = useRef(rows.length)

  // One rule: the clicked header stays EXACTLY where it was on screen. The
  // content grows and shrinks under it. No jump to the bottom — scrolling the
  // dispatcher did not ask for reads as a layout shift.
  useLayoutEffect(() => {
    const pending = slickAIToggleRef.current
    if (!pending) return
    slickAIToggleRef.current = null

    if (!ref.current) return

    // The last group in the chat has nothing below it, so the list has nowhere
    // to scroll: anchoring hits the end stop and throws the view upwards. The
    // bottom is the natural reference point there. Placeholders (composing, sync
    // error) are pushed after the folding, so "last" means last real row.
    const index = rows.findIndex(
      (row) =>
        !isPlaceholder(row) &&
        isSlickAIGroup(row) &&
        row.groupId === pending.groupId,
    )
    let lastRowIndex = rows.length - 1
    while (lastRowIndex >= 0 && isPlaceholder(rows[lastRowIndex]))
      lastRowIndex--
    if (index === lastRowIndex) {
      scrollToBottom(false)
      return
    }

    const element = slickAIGroupElement(pending.groupId)
    if (!element) return

    const delta = element.getBoundingClientRect().top - pending.screenTop
    if (Math.abs(delta) > 1) {
      ref.current.scrollBy(delta)
    }
  }, [expandedSlickAIGroups, rows, scrollToBottom])

  const newestMessageId = layerFilteredMessages.at(-1)?.id
  const prevNewestMessageId = useRef(newestMessageId)

  useEffect(() => {
    // The composing row turning into the answer keeps the row count, so the
    // newest message is watched too. The jump is deliberate: a smooth scroll
    // to rows virtua hasn't measured yet stops short and unsticks the view.
    const newestMessageChanged = newestMessageId !== prevNewestMessageId.current
    if (
      ref.current &&
      shouldStickToBottom.current &&
      (rows.length > prevRowsLength.current || newestMessageChanged) &&
      isTabVisible
    ) {
      shifting.current = false
      scrollToBottom(false)
      checkIfMarkMessageAsDisplayed()
    }
    // Update previous length *after* the check
    prevRowsLength.current = rows.length
    prevNewestMessageId.current = newestMessageId
  }, [
    rows.length,
    newestMessageId,
    scrollToBottom,
    checkIfMarkMessageAsDisplayed,
    isTabVisible,
  ])

  useEffect(() => {
    if (!messages || !messages.length || messages.length === 0) return
    const lastMessage = messages[messages.length - 1]
    if (
      ref.current &&
      ChatHelpers.isChatWithAssistant(chat) &&
      ChatHelpers.isMessageFromSlickAI(lastMessage.from) &&
      isTabVisible
    ) {
      shifting.current = false
      scrollToBottom(true) // Smooth scroll for new incoming messages
      checkIfMarkMessageAsDisplayed()
    }
    prevRowsLength.current = rows.length
  }, [
    scrollToBottom,
    checkIfMarkMessageAsDisplayed,
    rows,
    chat,
    messages,
    isTabVisible,
  ])

  useLayoutEffect(() => {
    if (!startFetching && effectEnabled.current) {
      shifting.current = false
    }
  })

  useEffect(() => {
    try {
      const measure = performance.measure("ChatChange.duration", {
        start: "ChatChange",
        detail: {
          jid: chat.jid,
        },
      })
      performance.clearMarks("ChatChange")
      performance.clearMeasures("ChatChange.duration")
      console.log(`ChatChange took ${measure.duration.toFixed(2)}ms`)
    } catch (error) {
      // When rendering after page load the mark does not exist and measuring fails
      // This is expected
    }
  }, [chat.jid])

  // This is needed to auto-fetch more messages if the initial load doesn't fill the viewport
  useEffect(() => {
    const checkAndFetchIfNeeded = async () => {
      // Skip if not ready or already fetching
      if (
        !ready.current ||
        !ref.current ||
        startFetching ||
        backfillingRef.current ||
        !isRoomSyncFinished
      ) {
        return
      }

      // Check if we have a scrollbar (content height > viewport height)
      const hasScrollbar = ref.current.scrollSize > ref.current.viewportSize

      // If no scrollbar and we haven't fetched for this message count yet
      if (!hasScrollbar && startFetchedCountRef.current < rows.length) {
        console.log(
          "No scrollbar detected, fetching more messages automatically",
        )

        // Mark current message count to avoid duplicate fetch
        startFetchedCountRef.current = rows.length

        // Enable shift mode and start fetching. Nobody asked for this page —
        // it only tops the viewport up — so it stays quiet: no spinner over
        // the conversation and no locked composer.
        shifting.current = true
        backfillingRef.current = true

        try {
          await onFetchMoreMessages()
        } finally {
          backfillingRef.current = false
        }
      }
    }

    // Delay the check to ensure VList has rendered properly
    const timeoutId = setTimeout(checkAndFetchIfNeeded, 100)

    return () => clearTimeout(timeoutId)
  }, [rows.length, startFetching, isRoomSyncFinished, onFetchMoreMessages])

  // AudioPlayback state: only keep mounted the row that is currently playing audio
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null)

  const audioPlaybackContext = useMemo<AudioPlaybackContextType>(
    () => ({
      playingMessageId,
      play: (messageId: string) => setPlayingMessageId(messageId),
      stop: () => setPlayingMessageId(null),
    }),
    [playingMessageId],
  )

  const playingRowIndices = useMemo(() => {
    if (!playingMessageId) return []
    const index = rows.findIndex((row) =>
      rowContainsMessageId(row, playingMessageId),
    )
    return index >= 0 ? [index] : []
  }, [rows, playingMessageId])

  return (
    <AudioPlaybackContext.Provider value={audioPlaybackContext}>
      <ChatHistoryContext.Provider value={chatHistoryContext}>
        {/* Alerts */}

        {referenceRouteAlertVisible && <ReferenceRouteAlert />}
        {lateAlertVisible && <LateAlert />}
        {routeFinishedAlertVisible && <FinishAlert />}

        {/* Viewers */}

        {imageGalleryVisible && !imageGalleryExternalSource && (
          <ImageGallery
            messages={messages}
            directImages={imageGalleryDirectImages}
            metadataOverrides={imageGalleryMetadataOverrides}
          />
        )}
        {PDFViewerVisible && !PDFViewerExternalSource && <PDFViewer />}

        <DriverWorkspaceProvider value={splitDriverChat}>
          <DriverContext.Provider value={driverContext}>
            <div className="flex h-full flex-col">
              <ResizablePanelGroup
                key={
                  splitDriverChat
                    ? showWidgetColumn
                      ? stackLayout
                        ? "chat-two-v"
                        : "chat-two"
                      : "chat-one"
                    : undefined
                }
                // Only the workspace remembers a second column; the plain view
                // has always been a single saved layout, so leave it alone.
                autoSaveId={
                  splitDriverChat
                    ? stackLayout
                      ? "slickshift-chat-map-layout-v-mobile"
                      : "slickshift-chat-map-layout-v1"
                    : undefined
                }
                direction={
                  splitDriverChat && stackLayout && showWidgetColumn
                    ? "vertical"
                    : "horizontal"
                }
                className={splitDriverChat ? "min-h-0 flex-1" : "h-full"}
              >
                <ResizablePanel
                  defaultSize={
                    showDesktopMapPanel ? 60 : splitDriverChat ? 60 : 100
                  }
                  minSize={splitDriverChat && showWidgetColumn ? 40 : 30}
                >
                  {/* The plain view puts the column in a full-height row box;
                      that box is what gives the thread its height. The split
                      view sizes the column itself instead. */}
                  <div
                    className={cn(
                      splitDriverChat ? "contents" : "flex h-full flex-row",
                    )}
                  >
                    <div
                      className={cn(
                        css.chatHistoryWrapper,
                        splitDriverChat &&
                          "h-full min-w-0 bg-sidebar transition-colors",
                      )}
                    >
                      {splitDriverChat ? (
                        <>
                          <ChatHeader
                            minimal
                            chat={chat}
                            onReturnToChatLists={onReturnToChatLists}
                            onOpenChatInfo={onOpenChatInfo}
                            onActiveChatChange={onActiveChatChange}
                          />
                          <DriverOverviewSection
                            chatJid={chat.jid}
                            routeJid={routeJid}
                            location={currentLocation}
                          />
                        </>
                      ) : (
                        showHeader && (
                          <ChatHeader
                            chat={chat}
                            onReturnToChatLists={onReturnToChatLists}
                            onOpenChatInfo={onOpenChatInfo}
                            onActiveChatChange={onActiveChatChange}
                          />
                        )
                      )}

                      <PinnedMessage chat={chat} key={chat.jid} />

                      {startFetching && (
                        <Loader
                          small
                          className="small pointer-events-none absolute inset-0 z-50 flex items-center justify-center"
                        />
                      )}

                      {!isMobile && activeTab === TAB_TYPES.CHATS && (
                        <AnalyzeOrderProvider>
                          <DropZone onFileDropped={onFileDropped} />
                          <AnalyzeOrder onSend={onSend} />
                        </AnalyzeOrderProvider>
                      )}

                      <div
                        id="scrollableDiv"
                        className="relative flex flex-grow cursor-default flex-col content-end overflow-x-hidden scrollbar-borderless"
                        ref={containerRef}
                      >
                        {isAIChat ? (
                          <AIChatView
                            chat={chat}
                            isRoomSyncFinished={isRoomSyncFinished}
                            onSendMessage={(text) =>
                              onSend(text, new DataTransfer().files)
                            }
                            onSelectPrompt={(text) => {
                              chatInputRef.current?.setText(text)
                            }}
                          />
                        ) : (
                          <VList
                            ref={ref}
                            overscan={10}
                            style={{
                              flex: 1,
                              overflowX: "hidden",
                              scrollbarGutter: "stable",
                            }}
                            reverse
                            shift={shifting.current}
                            keepMounted={playingRowIndices}
                            onScroll={async () => {
                              // Skip if component isn't fully initialized or we're scrolling via the button
                              if (!ready.current) return
                              if (!ref.current) return
                              if (isScrollingNotByUser.current) return

                              // Determine if we're at the bottom (within 1.5px tolerance)
                              // This controls automatic scrolling behavior for new messages
                              shouldStickToBottom.current =
                                ref.current.scrollOffset -
                                  ref.current.scrollSize +
                                  ref.current.viewportSize >=
                                -1.5

                              // Check if the last visible message should be marked as read
                              checkIfMarkMessageAsDisplayed()

                              // Update UI state to reflect scrolling activity
                              setIsScrolling(true)

                              // Track which message is at the top of the viewport
                              // Used for displaying the date marker
                              setFirstVisibleIndex(ref.current.findStartIndex())

                              // Handle infinite scrolling / pagination logic
                              // When user scrolls near the top of the list and we haven't already
                              // fetched messages for this scroll position
                              if (
                                shouldFetchOlderMessages({
                                  startIndex: ref.current.findStartIndex(),
                                  rowCount: count,
                                  alreadyFetchedCount:
                                    startFetchedCountRef.current,
                                  scrollOffset: ref.current.scrollOffset,
                                  scrollSize: ref.current.scrollSize,
                                  viewportSize: ref.current.viewportSize,
                                })
                              ) {
                                // Mark current message count to avoid duplicate fetch
                                startFetchedCountRef.current = count

                                // Enable shift mode to maintain scroll position during loading
                                shifting.current = true

                                // Show loading indicator
                                setStartFetching(true)
                                // Load older messages
                                await onFetchMoreMessages()

                                // Hide loading indicator when done
                                setStartFetching(false)
                              }
                            }}
                            onScrollEnd={() => {
                              if (ref.current) {
                                const onBottom = (shouldStickToBottom.current =
                                  ref.current.scrollOffset -
                                    ref.current.scrollSize +
                                    ref.current.viewportSize >=
                                  -1.5)
                                setShowScrollToBottomButton(!onBottom)
                                setIsScrolling(false)
                              }
                            }}
                          >
                            {rows.map((row, index) =>
                              renderVirtualRow(row, index),
                            )}
                          </VList>
                        )}
                        {currentLocation && (
                          <ShowMobileMapButton
                            shouldMoveUp={showScrollToBottomButton}
                          />
                        )}
                        {showDesktopMapButton && (
                          <ShowDesktopMapButton
                            onShow={() => setMapHidden(false)}
                          />
                        )}
                        {showScrollToBottomButton && (
                          <ScrollBottomButton
                            chat={chat}
                            onClick={handleScrollBottomButton}
                            disabled={startFetching}
                          />
                        )}
                        {/* The split view dates the thread with an inline
                            line inside the messages; everywhere else this
                            floating pill is it. */}
                        {!splitDriverChat &&
                          ref.current &&
                          firstVisibleIndex !== null && (
                            <TopDayMarker
                              isScrolling={isScrolling}
                              topVisibleMessage={rows[firstVisibleIndex]}
                            />
                          )}
                      </div>
                      {!isRoomSyncFinished && (
                        <Loader
                          small
                          className="small absolute bottom-[105px] flex w-full justify-center"
                        />
                      )}
                      <div
                        className={cn(
                          css.chatInput,
                          isAIChat ? "bg-background" : undefined,
                        )}
                        style={
                          splitDriverChat
                            ? // Line the composer's bottom edge up with the driver
                              // chat widget's composer on the right.
                              {
                                paddingLeft: 28,
                                paddingRight: 28,
                                paddingBottom: 21,
                              }
                            : undefined
                        }
                      >
                        <div
                          className={
                            isAIChat
                              ? "mx-auto w-full max-w-[800px]"
                              : undefined
                          }
                        >
                          {/* Message Selection Bar */}
                          {messageForwarding.isForwardingMode && (
                            <MessageSelectionBar
                              selectedCount={
                                messageForwarding.selectedMessageIds.size
                              }
                              onCancel={messageForwarding.exitForwardingMode}
                              onForward={
                                messageForwarding.handleForwardMessages
                              }
                            />
                          )}
                          <ChatInput
                            key={chat.jid}
                            onSend={onSend}
                            onSendAgent={
                              splitDriverChat ? handleAgentSend : undefined
                            }
                            chat={chat}
                            useAssistantPlaceholder={ftueEnabled}
                            ref={chatInputRef}
                          />
                          {ftueEnabled && (
                            <p className="mt-1.5 px-1 text-center text-xs text-muted-foreground">
                              <Trans
                                i18nKey="app.chatHistory.bookmarkHint"
                                components={{
                                  b: <span className="font-medium" />,
                                }}
                              />
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </ResizablePanel>
                <ChatSidePanel
                  chat={chat}
                  routeJid={routeJid}
                  containerRef={containerRef}
                  showWidgets={showWidgetColumn}
                  showMap={showDesktopMapPanel}
                  onHideMap={
                    hideMapButtonEnabled ? () => setMapHidden(true) : undefined
                  }
                  driverVisibleMessages={driverVisibleMessages}
                  onSend={onSend}
                  chatInputRef={chatInputRef}
                />
              </ResizablePanelGroup>
            </div>
            {routeJid && mapOnMobile && (isMobile || isSmallScreen) && (
              <ChatMap
                chat={chat}
                driverJid={routeJid}
                chatContainerRef={containerRef}
              />
            )}
          </DriverContext.Provider>
        </DriverWorkspaceProvider>

        {/* Forward Message Dialog */}

        <ForwardMessageDialog
          open={messageForwarding.showForwardDialog}
          onClose={messageForwarding.handleCloseForwardDialog}
          onForward={messageForwarding.handleForwardToChat}
        />
      </ChatHistoryContext.Provider>
    </AudioPlaybackContext.Provider>
  )
}

ChatHistory.displayName = "ChatHistory"

export default Sentry.withProfiler(ChatHistory)
